import { Switch, Route, Redirect } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/contexts/AuthContext.js";
import { DashboardProvider } from "@/contexts/DashboardContext.js";
import { FeatureFlagProvider } from "@/contexts/FeatureFlagContext.js";
import { PlanProvider } from "@/contexts/PlanContext.js";
import { SpacesProvider } from "@/hooks/useSpaces.js";
import { Layout } from "@/components/Layout.js";
import { ToastProvider } from "@/components/Toast.js";
import OnboardingFlow from "@/features/onboarding/OnboardingFlow.js";
import HomePage from "@/features/home/HomePage.js";
import InventoryPage from "@/features/inventory/InventoryPage.js";
import SpacesPage from "@/features/spaces/SpacesPage.js";
import SpaceDetailPage from "@/features/spaces/SpaceDetailPage.js";
import OrganizeHubPage from "@/features/organize/OrganizeHubPage.js";
import PlacementPage from "@/features/organize/PlacementPage.js";
import FindHomesPage from "@/features/organize/FindHomesPage.js";
import EventsPage from "@/features/organize/EventsPage.js";
import KeepItUpPage from "@/features/organize/KeepItUpPage.js";
import StartPage from "@/features/organize/StartPage.js";
import RoutinesPage from "@/features/organize/RoutinesPage.js";
import QuickResetPage from "@/features/organize/QuickResetPage.js";
import OrganizeListsPage from "@/features/organize/OrganizeListsPage.js";
import TriagePage from "@/features/organize/TriagePage.js";
import MovesPage from "@/features/organize/MovesPage.js";
import OrganizeGoalsPage from "@/features/organize/OrganizeGoalsPage.js";
import GoalDetailPage from "@/features/organize/GoalDetailPage.js";
import GoalHistoryPage from "@/features/organize/GoalHistoryPage.js";
import SettingsPage from "@/features/settings/SettingsPage.js";
import TagsPage from "@/features/settings/TagsPage.js";
import PricingPage from "@/features/billing/PricingPage.js";
import DesignSystemPage from "@/features/design-system/DesignSystemPage.js";
import DesignSystemV2Page from "@/features/design-system/DesignSystemV2Page.js";
import AdminPage from "@/features/admin/AdminPage.js";
import NotificationsPage from "@/features/notifications/NotificationsPage.js";
import DeclutterPage from "@/features/declutter/DeclutterPage.js";
import PackingListsPage from "@/features/packing/PackingListsPage.js";
import BoxesPage from "@/features/boxes/BoxesPage.js";
import ValuationPage from "@/features/valuation/ValuationPage.js";
import VerifyEmailPage from "@/features/auth/VerifyEmailPage.js";
import ForgotPasswordPage from "@/features/auth/ForgotPasswordPage.js";
import ResetPasswordPage from "@/features/auth/ResetPasswordPage.js";
import { ErrorBoundary } from "@/components/ErrorBoundary.js";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" /></div>;
  if (!user) return <OnboardingFlow />;
  return (
    <DashboardProvider>
      <SpacesProvider>
      <FeatureFlagProvider>
        <PlanProvider>
          <Layout>
            <Switch>
              <Route path="/" component={HomePage} />
              <Route path="/inventory" component={InventoryPage} />
              <Route path="/spaces" component={SpacesPage} />
              <Route path="/spaces/:id" component={SpaceDetailPage} />
              <Route path="/organize" component={OrganizeHubPage} />
              {/* Start something — the launcher surface (Option C: split from the status hub) */}
              <Route path="/organize/start" component={StartPage} />
              {/* Category landings (Placement · Events · Keep It Up) */}
              <Route path="/organize/placement" component={PlacementPage} />
              <Route path="/organize/find-homes" component={FindHomesPage} />
              <Route path="/organize/events" component={EventsPage} />
              <Route path="/organize/upkeep" component={KeepItUpPage} />
              <Route path="/organize/routines" component={RoutinesPage} />
              <Route path="/organize/quick-reset" component={QuickResetPage} />
              <Route path="/organize/lists" component={OrganizeListsPage} />
              <Route path="/organize/moves">{() => <ErrorBoundary><MovesPage /></ErrorBoundary>}</Route>
              <Route path="/organize/triage">{() => <ErrorBoundary><TriagePage /></ErrorBoundary>}</Route>
              <Route path="/organize/goals">{() => <ErrorBoundary><OrganizeGoalsPage /></ErrorBoundary>}</Route>
              <Route path="/organize/goals/history">{() => <ErrorBoundary><GoalHistoryPage /></ErrorBoundary>}</Route>
              <Route path="/organize/goals/:id">{(params) => <ErrorBoundary key={params.id}><GoalDetailPage /></ErrorBoundary>}</Route>
              <Route path="/organize/declutter" component={DeclutterPage} />
              <Route path="/organize/packing" component={PackingListsPage} />
              {/* Legacy redirect: /organize/place renamed to /organize/find-homes ("Give things a home") */}
              <Route path="/organize/place"><Redirect to="/organize/find-homes" /></Route>
              {/* Legacy redirects: /workflows -> /organize */}
              <Route path="/workflows/declutter"><Redirect to="/organize/declutter" /></Route>
              <Route path="/workflows/packing"><Redirect to="/organize/packing" /></Route>
              <Route path="/workflows/routines"><Redirect to="/organize/routines" /></Route>
              <Route path="/workflows">{() => <Redirect to="/organize" />}</Route>
              <Route path="/settings" component={SettingsPage} />
              <Route path="/settings/tags" component={TagsPage} />
              <Route path="/settings/notifications" component={NotificationsPage} />
              <Route path="/pricing" component={PricingPage} />
              <Route path="/boxes" component={BoxesPage} />
              <Route path="/valuation" component={ValuationPage} />
              <Route path="/admin" component={AdminPage} />
            </Switch>
          </Layout>
        </PlanProvider>
      </FeatureFlagProvider>
      </SpacesProvider>
    </DashboardProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <Switch>
            <Route path="/design-system" component={DesignSystemPage} />
            <Route path="/design-system-v2" component={DesignSystemV2Page} />
            <Route path="/verify-email" component={VerifyEmailPage} />
            <Route path="/forgot-password" component={ForgotPasswordPage} />
            <Route path="/reset-password" component={ResetPasswordPage} />
            <Route><AppRoutes /></Route>
          </Switch>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
